import _fromPairs from "lodash/fromPairs";
import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { OutlinedButton } from "common/DS/Buttons";
import { Caption } from "common/DS/Text";
import { questionProps, userShape } from "lib/propTypes";
import {
    DEFAULT_MULTI_SELECT_MIN_SELECTIONS,
    DEFAULT_MULTI_SELECT_MAX_SELECTIONS,
    MULTI_SELECT_FOLLOW_UP_DELIMETER,
} from "lib/constants";
import { slugify } from "lib/util";

import AnswerDisplay from "./AnswerDisplay";
import QuestionButtonGroup from "./QuestionButtonGroup";

const MultiSelectFollowUp = (
    {
        follow_up: followUpText,
        follow_up_options: followUpOptions,
        handleBack,
        handleChange,
        handleSubmit,
        handleSkip,
        optional,
        visible,
        isLastQuestion,
    },
    { t: _ },
) => {
    const { options } = followUpOptions;

    const [selected, setSelected] = useState(_fromPairs(options.map(({ text }) => [text, false])));
    const [isDirty, setIsDirty] = useState(false);

    // Set appropriate defaults for min/max if not specified
    const minSelections = followUpOptions.min_selections ?? DEFAULT_MULTI_SELECT_MIN_SELECTIONS;
    const maxSelections = followUpOptions.max_selections ?? DEFAULT_MULTI_SELECT_MAX_SELECTIONS;

    // Filter to only selected entries and return the value keys delimited by vertical bar
    const renderSelectedValue = () =>
        Object.entries(selected)
            .filter(([, isSelected]) => isSelected)
            .map(([value]) => value)
            .join(MULTI_SELECT_FOLLOW_UP_DELIMETER);

    // Toggle the given selection value within the selected state
    const handleSelect = (value) => {
        if (!isDirty) setIsDirty(true);
        setSelected((prev) => ({ ...prev, [value]: !prev[value] }));

        if (handleChange) {
            handleChange(renderSelectedValue());
        }
    };

    const createOnSelect = useCallback(
        (value) =>
            ({ target }) => {
                target.blur();
                handleSelect(value);
            },
        [],
    );

    const onSubmit = () => {
        if (handleSubmit) {
            handleSubmit(renderSelectedValue());
        }
    };

    useEffect(() => {
        if (visible && handleChange) {
            handleChange(renderSelectedValue());
        }
    }, [visible]);

    const minMaxText = useMemo(() => {
        if (minSelections >= 2 && maxSelections >= 1) {
            return _("Choose between {minSelections} and {maxSelections}", {
                minSelections,
                maxSelections,
            });
        }
        if (minSelections > 1 && !maxSelections) {
            return _("Choose at least {minSelections}", { minSelections });
        }
        if (minSelections <= 1 && maxSelections >= 1) {
            return _("Choose up to {maxSelections}", { maxSelections });
        }
        return null;
    }, [minSelections, maxSelections]);

    // Selected and error states
    const totalSelected = Object.values(selected).filter(Boolean).length;
    const minSelected = totalSelected >= minSelections;
    const maxSelected = maxSelections && totalSelected >= maxSelections;
    const showError = isDirty && !optional && !minSelected;
    const errorMsg = showError ? _("Choose at least {minSelections}", { minSelections }) : null;

    // Display based state values
    // If <= 5 options then just one column of options, otherwise two columns split evenly
    const midpoint = Math.round(options.length / 2);
    const columns =
        options.length <= 5 ? [options] : [options.slice(0, midpoint), options.slice(midpoint)];

    // If not visible then just hide so we can maintain existing component state
    const style = {};
    if (!visible) {
        style.display = "none";
    }

    return (
        <div
            className="questions__follow-up questions__follow-up-multi-select label-group"
            style={style}
        >
            <label>{followUpText}</label>
            {minMaxText && (
                <Caption className="questions__follow-up-label-caption">{minMaxText}</Caption>
            )}
            <div
                className={classNames("questions__follow-up-multi-select-group", {
                    "questions__follow-up-multi-select-group--single-col": columns.length === 1,
                })}
            >
                {columns.map((colOptions, idx) => (
                    <div
                        className="questions__follow-up-multi-select-group-column"
                        key={`options-col-${idx}`}
                    >
                        {colOptions.map(({ text }) => (
                            <OutlinedButton
                                key={slugify(text)}
                                onClick={createOnSelect(text)}
                                selected={selected[text]}
                                size="large"
                                disabled={!selected[text] && maxSelected}
                            >
                                <AnswerDisplay text={text} />
                            </OutlinedButton>
                        ))}
                    </div>
                ))}
            </div>
            <div className="help-block">{errorMsg}</div>
            <QuestionButtonGroup
                nextDisabled={!minSelected}
                handleSkip={handleSkip}
                handleSubmit={onSubmit}
                handleBack={handleBack}
                isLastQuestion={isLastQuestion}
                forceShowSkip={optional}
            />
        </div>
    );
};

MultiSelectFollowUp.contextTypes = {
    t: PropTypes.func,
};

MultiSelectFollowUp.propTypes = {
    /* eslint-disable react/require-default-props */
    // These actually are required in the prop types but eslint can't tell
    follow_up: questionProps.follow_up,
    /* eslint-enable react/require-default-props */
    follow_up_options: questionProps.follow_up_options.isRequired,
    selectedAnswerData: PropTypes.shape({ response: PropTypes.string }),
    handleBack: PropTypes.func,
    handleChange: PropTypes.func,
    handleSubmit: PropTypes.func,
    handleSkip: PropTypes.func,
    optional: PropTypes.bool,
    visible: PropTypes.bool,
    isLastQuestion: PropTypes.bool,
    extraData: PropTypes.shape({
        user: userShape,
        hire_id: PropTypes.number,
    }),
};

MultiSelectFollowUp.defaultProps = {
    selectedAnswerData: null,
    handleBack: null,
    handleChange: null,
    handleSubmit: null,
    handleSkip: null,
    optional: false,
    visible: true,
    isLastQuestion: false,
    extraData: {},
};

export default React.memo(MultiSelectFollowUp);
